import { getContrast, parseToHsl, parseToRgb, hslToColorString } from 'polished';

interface RgbColor {
  red: number;
  green: number;
  blue: number;
}

/**
 * Blends a foreground color at a given opacity over a background color.
 * Returns the blended color as a hex string.
 */
export function blendWithOpacity(fgHex: string, bgHex: string, opacity: number): string {
  const fg: RgbColor = parseToRgb(fgHex);
  const bg: RgbColor = parseToRgb(bgHex);

  const r = Math.round(fg.red * opacity + bg.red * (1 - opacity));
  const g = Math.round(fg.green * opacity + bg.green * (1 - opacity));
  const b = Math.round(fg.blue * opacity + bg.blue * (1 - opacity));

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b
    .toString(16)
    .padStart(2, '0')}`;
}

/**
 * Returns the WCAG contrast ratio between a foreground color at a given opacity
 * and a background color.
 */
export function getContrastWithOpacity(fgHex: string, bgHex: string, opacity: number): number {
  const blended = blendWithOpacity(fgHex, bgHex, opacity);
  return getContrast(blended, bgHex);
}

/**
 * Finds the nearest shade of `colorHex` that reaches `minRatio` contrast
 * against `bgHex`. Keeps the HSL hue and saturation, changes only lightness.
 * Searches in both directions from the original in 0.1 percentage-point steps.
 * Falls back to #000000 or #ffffff at the range ends.
 */
export function findNearestAccessibleShade(
  colorHex: string,
  bgHex: string,
  minRatio = 4.5,
): string {
  const hsl = parseToHsl(colorHex);
  const originalLightness = hsl.lightness;

  // Search both directions simultaneously in 0.1pp steps
  for (let delta = 0.001; delta <= 1; delta += 0.001) {
    // Try darker
    const darkerL = originalLightness - delta;
    if (darkerL >= 0) {
      const candidate = hslToColorString({ ...hsl, lightness: darkerL });
      if (getContrast(candidate, bgHex) >= minRatio) {
        return candidate;
      }
    }

    // Try lighter
    const lighterL = originalLightness + delta;
    if (lighterL <= 1) {
      const candidate = hslToColorString({ ...hsl, lightness: lighterL });
      if (getContrast(candidate, bgHex) >= minRatio) {
        return candidate;
      }
    }
  }

  // Fallback: whichever endpoint contrasts more
  const blackContrast = getContrast('#000000', bgHex);
  const whiteContrast = getContrast('#ffffff', bgHex);
  return blackContrast >= whiteContrast ? '#000000' : '#ffffff';
}

export interface AccessibleTabColors {
  inactiveOpacity: number;
  inactiveColor: string;
  activeColor: string;
}

/**
 * Computes accessible tab colors using the three-way rule:
 * 1. Try opacity 0.6 — if it reaches 4.5:1, use it
 * 2. Try opacity 1 with the original text color — if it reaches 4.5:1, use it
 * 3. Opacity 1 with the nearest same-hue shade that reaches 4.5:1
 *
 * For the active (selected) tab: keep the hover color if it reaches 4.5:1,
 * otherwise find the nearest same-hue shade.
 *
 * Wraps polished calls in try/catch — if a color string is invalid,
 * returns the original colors unchanged with opacity 0.6.
 */
export function computeAccessibleTabColors(
  textColor: string,
  hoverColor: string,
  bgColor: string,
): AccessibleTabColors {
  try {
    // Determine inactive tab opacity and color
    let inactiveOpacity = 0.6;
    let inactiveColor = textColor;

    if (getContrastWithOpacity(textColor, bgColor, 0.6) >= 4.5) {
      // Branch 1: 0.6 opacity passes
      inactiveOpacity = 0.6;
      inactiveColor = textColor;
    } else if (getContrast(textColor, bgColor) >= 4.5) {
      // Branch 2: opacity 1 with original text color passes
      inactiveOpacity = 1;
      inactiveColor = textColor;
    } else {
      // Branch 3: need a replacement shade
      inactiveOpacity = 1;
      inactiveColor = findNearestAccessibleShade(textColor, bgColor);
    }

    // Determine active tab color
    const activeColor =
      getContrast(hoverColor, bgColor) >= 4.5
        ? hoverColor
        : findNearestAccessibleShade(hoverColor, bgColor);

    return { inactiveOpacity, inactiveColor, activeColor };
  } catch {
    // Invalid color string — return current behavior unchanged
    return {
      inactiveOpacity: 0.6,
      inactiveColor: textColor,
      activeColor: hoverColor,
    };
  }
}
